import { useEffect, useState } from 'react'
import { fetchShallowHealth, type HealthStatus } from '@/application/health/api'
import { BACKEND_ENABLED } from '@/infrastructure/config/featureFlags'

type HealthState =
  | { status: 'loading' }
  | { status: HealthStatus }

/**
 * Polls /api/health on a fixed interval and returns the latest
 * status. Pauses while the document is hidden so a backgrounded tab
 * doesn't keep hammering the backend.
 *
 * If the backend is unreachable, the hook reports 'down' — the
 * offline review surface keeps working from IDB regardless.
 */
export function useHealth(intervalMs: number = 30_000): HealthState {
  const [state, setState] = useState<HealthState>({ status: 'loading' })

  useEffect(() => {
    // Backend disabled: do not poll at all. State stays in 'loading'
    // but the only consumer (HealthPill) renders null in that mode.
    if (!BACKEND_ENABLED) return
    let cancelled = false
    const controller = new AbortController()

    function refresh() {
      void fetchShallowHealth(controller.signal).then((result) => {
        if (cancelled) return
        setState({ status: result.status })
      })
    }

    function onVisibility() {
      if (!document.hidden) refresh()
    }

    refresh()
    const intervalId = window.setInterval(() => {
      if (!document.hidden) refresh()
    }, intervalMs)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      controller.abort()
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [intervalMs])

  return state
}
